import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bug, Zap, AlertCircle, X, Plus, Users, MessageSquare,
  CheckCircle2, Clock, RefreshCw, XCircle, Search, Bell, BellOff,
  Globe, EyeOff, Tag, Star, ThumbsUp, Timer, Link2,
} from "lucide-react";
import api from "../services/api";

const RESOLUTION_TIMES = ['< 1 day', '1-3 days', '4-7 days', '1-2 weeks', '2-4 weeks', '1+ months'];

function StarRating({ value, onChange, label }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: "#475569", marginBottom: 5 }}>{label}</div>
      <div style={{ display: "flex", gap: 4 }}>
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n}
            type="button"
            onMouseEnter={() => setHovered(n)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => onChange(n === value ? 0 : n)}
            style={{ border: "none", background: "transparent", cursor: "pointer", padding: 2, display: "flex" }}
          >
            <Star
              size={22}
              fill={(hovered || value) >= n ? "#f59e0b" : "none"}
              color={(hovered || value) >= n ? "#f59e0b" : "#d1d5db"}
              strokeWidth={1.5}
            />
          </button>
        ))}
        {value > 0 && (
          <span style={{ fontSize: 12, color: "#94a3b8", alignSelf: "center", marginLeft: 4 }}>{value}/5</span>
        )}
      </div>
    </div>
  );
}

const PLATFORMS = [
  { value: "XEBO",       label: "XEBO",       color: "#6366f1", bg: "#eef2ff" },
  { value: "SURVEYTOGO", label: "SurveyToGo", color: "#0d9488", bg: "#f0fdfa" },
  { value: "FORSTA",     label: "Forsta",     color: "#d97706", bg: "#fffbeb" },
];

const ISSUE_TYPES = [
  { value: "BUG",   label: "Bug",               icon: Bug,          color: "#ef4444" },
  { value: "LOGIC", label: "Challenging Logic",  icon: Zap,          color: "#f59e0b" },
  { value: "OTHER", label: "Other",              icon: AlertCircle,  color: "#64748b" },
];

const STATUSES = [
  { value: "OPEN",        label: "Open",        icon: AlertCircle,  color: "#ef4444", bg: "#fef2f2" },
  { value: "IN_PROGRESS", label: "In Progress", icon: Clock,        color: "#f59e0b", bg: "#fffbeb" },
  { value: "RESOLVED",    label: "Resolved",    icon: CheckCircle2, color: "#16a34a", bg: "#f0fdf4" },
  { value: "CLOSED",      label: "Closed",      icon: XCircle,      color: "#64748b", bg: "#f8fafc" },
];

const getPlatform  = v => PLATFORMS.find(p => p.value === v)   || PLATFORMS[0];
const getStatus    = v => STATUSES.find(s => s.value === v)    || STATUSES[0];
const getIssueType = v => ISSUE_TYPES.find(t => t.value === v) || ISSUE_TYPES[2];

function StatusBadge({ status }) {
  const s = getStatus(status);
  const Icon = s.icon;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, borderRadius: 100, padding: "3px 10px", fontSize: 11.5, fontWeight: 600, background: s.bg, color: s.color }}>
      <Icon size={11} strokeWidth={2.5} /> {s.label}
    </span>
  );
}

function PlatformChip({ platform }) {
  const p = getPlatform(platform);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700, background: p.bg, color: p.color, letterSpacing: "0.03em" }}>
      {p.label}
    </span>
  );
}

function TypeChip({ type }) {
  const t = getIssueType(type);
  const Icon = t.icon;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600, background: "#f8fafc", color: t.color, border: "1px solid #e2e8f0" }}>
      <Icon size={10} /> {t.label}
    </span>
  );
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso), now = new Date();
  const diffMins = Math.floor((now - d) / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const h = Math.floor(diffMins / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

// ── Category autocomplete input ─────────────────────────────────────────────
function CategoryInput({ value, onChange, existingCategories }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  const suggestions = existingCategories.filter(
    c => c.toLowerCase().includes(value.toLowerCase()) && c.toLowerCase() !== value.toLowerCase()
  );

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div style={{ position: "relative" }}>
        <Tag size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
        <input
          value={value}
          onChange={e => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="e.g. Loop & Fill, Piping, Quota…"
          style={{ width: "100%", boxSizing: "border-box", paddingLeft: 30, paddingRight: 12, paddingTop: 9, paddingBottom: 9, borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13.5, color: "#0f172a", outline: "none", fontFamily: "inherit", background: "#fff" }}
        />
      </div>
      {open && suggestions.length > 0 && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 50, marginTop: 2, maxHeight: 180, overflowY: "auto" }}>
          {suggestions.map(c => (
            <div key={c} onClick={() => { onChange(c); setOpen(false); }} style={{ padding: "8px 12px", fontSize: 13, color: "#0f172a", cursor: "pointer" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              {c}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Segment Picker ──────────────────────────────────────────────────────────
function SegmentPicker({ linked, onLink, onUnlink }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef();
  const ref = useRef();

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setResults([]); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const search = v => {
    setQuery(v);
    clearTimeout(debounceRef.current);
    if (v.trim().length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`api/platform-issues/issues/search_segments/?q=${encodeURIComponent(v.trim())}`);
        const linkedIds = new Set((linked || []).map(s => s.id));
        setResults((res.data || []).filter(s => !linkedIds.has(s.id)));
      } catch { } finally { setLoading(false); }
    }, 300);
  };

  return (
    <div ref={ref}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8, display: "flex", alignItems: "center", gap: 5 }}>
        <Link2 size={11} /> Affected Projects / Segments
      </div>
      {linked && linked.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {linked.map(s => (
            <span key={s.id} style={{ display: "inline-flex", alignItems: "center", gap: 5, borderRadius: 7, padding: "3px 10px", fontSize: 11.5, fontWeight: 600, background: "#eef2ff", color: "#4338ca", border: "1px solid #c7d2fe" }}>
              {s.project_name ? `${s.project_name}${s.segment_name ? ` — ${s.segment_name}` : ""}` : s.segment_name || `#${s.id}`}
              {s.job_number && <span style={{ color: "#94a3b8", fontWeight: 400 }}>({s.job_number})</span>}
              <button type="button" onClick={() => onUnlink(s.id)} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, display: "flex", color: "#6366f1" }}><X size={11} /></button>
            </span>
          ))}
        </div>
      )}
      <div style={{ position: "relative" }}>
        <Search size={12} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
        <input
          value={query}
          onChange={e => search(e.target.value)}
          placeholder="Search project or segment name, job number…"
          style={{ width: "100%", boxSizing: "border-box", paddingLeft: 30, paddingRight: 12, paddingTop: 8, paddingBottom: 8, borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, color: "#0f172a", outline: "none", fontFamily: "inherit", background: "#fff" }}
        />
      </div>
      {(loading || results.length > 0) && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", marginTop: 3, maxHeight: 200, overflowY: "auto" }}>
          {loading && <div style={{ padding: "10px 14px", fontSize: 12.5, color: "#94a3b8" }}>Searching…</div>}
          {results.map(s => (
            <div key={s.id} onClick={() => { onLink(s); setQuery(""); setResults([]); }}
              style={{ padding: "9px 14px", fontSize: 12.5, color: "#0f172a", cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ fontWeight: 600 }}>{s.project_name}{s.segment_name ? ` — ${s.segment_name}` : ""}</div>
              {s.job_number && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>{s.job_number}</div>}
            </div>
          ))}
          {!loading && results.length === 0 && query.length >= 2 && (
            <div style={{ padding: "10px 14px", fontSize: 12.5, color: "#94a3b8" }}>No matching segments found</div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Create Issue Modal ──────────────────────────────────────────────────────
function CreateIssueModal({ onClose, onCreated, existingCategories }) {
  const [form, setForm] = useState({ platform: "XEBO", issue_type: "BUG", category: "", title: "", description: "", workaround: "", is_published: false });
  const [linkedSegments, setLinkedSegments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleLinkSegment = seg => setLinkedSegments(prev => [...prev, seg]);
  const handleUnlinkSegment = id => setLinkedSegments(prev => prev.filter(s => s.id !== id));

  const handleSubmit = async () => {
    if (!form.title.trim())       { setError("Title is required."); return; }
    if (!form.description.trim()) { setError("Description is required."); return; }
    setSaving(true); setError("");
    try {
      const payload = { ...form, affected_segment_ids: linkedSegments.map(s => s.id) };
      const res = await api.post("api/platform-issues/issues/", payload);
      onCreated(res.data);
      onClose();
    } catch { setError("Failed to create issue. Please try again."); }
    finally { setSaving(false); }
  };

  const inputStyle = { width: "100%", boxSizing: "border-box", borderRadius: 8, border: "1px solid #e2e8f0", padding: "9px 12px", fontSize: 13.5, color: "#0f172a", outline: "none", fontFamily: "inherit", background: "#fff" };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 4, display: "block" };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 580, boxShadow: "0 20px 60px rgba(0,0,0,0.18)", overflow: "hidden" }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>Report Platform Issue</div>
          <button onClick={onClose} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#94a3b8", padding: 4, display: "flex", borderRadius: 6 }}><X size={18} /></button>
        </div>
        <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14, maxHeight: "72vh", overflowY: "auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>Platform *</label>
              <select value={form.platform} onChange={e => set("platform", e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                {PLATFORMS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Issue Type *</label>
              <select value={form.issue_type} onChange={e => set("issue_type", e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                {ISSUE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label style={labelStyle}>Category <span style={{ color: "#94a3b8", fontWeight: 400 }}>(optional — type your own or pick existing)</span></label>
            <CategoryInput value={form.category} onChange={v => set("category", v)} existingCategories={existingCategories} />
          </div>
          <div>
            <label style={labelStyle}>Title *</label>
            <input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Brief, clear description of the issue" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Description *</label>
            <textarea value={form.description} onChange={e => set("description", e.target.value)} placeholder="Steps to reproduce, expected vs actual behaviour, affected features…" rows={4} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }} />
          </div>
          <div>
            <label style={labelStyle}>Workaround / Partial Fix <span style={{ color: "#94a3b8", fontWeight: 400 }}>(optional)</span></label>
            <textarea value={form.workaround} onChange={e => set("workaround", e.target.value)} placeholder="Any known workaround or temporary fix…" rows={2} style={{ ...inputStyle, resize: "vertical", lineHeight: 1.5 }} />
          </div>
          {/* Segment picker */}
          <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 14px", border: "1px solid #e2e8f0" }}>
            <SegmentPicker linked={linkedSegments} onLink={handleLinkSegment} onUnlink={handleUnlinkSegment} />
          </div>
          {/* Publish toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: form.is_published ? "#f0fdfa" : "#f8fafc", borderRadius: 10, border: `1px solid ${form.is_published ? "#99f6e4" : "#e2e8f0"}`, cursor: "pointer" }}
            onClick={() => set("is_published", !form.is_published)}>
            <div style={{ width: 36, height: 20, borderRadius: 10, background: form.is_published ? "#0d9488" : "#e2e8f0", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
              <div style={{ position: "absolute", top: 2, left: form.is_published ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: form.is_published ? "#0d9488" : "#475569" }}>
                {form.is_published ? "Published — visible on home page" : "Publish to home page"}
              </div>
              <div style={{ fontSize: 11.5, color: "#94a3b8" }}>Everyone (especially DP team) will see this as an open issue</div>
            </div>
          </div>
          {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#dc2626" }}>{error}</div>}
        </div>
        <div style={{ padding: "16px 24px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "9px 18px", borderRadius: 8, border: "1px solid #e2e8f0", background: "white", color: "#64748b", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving} style={{ padding: "9px 18px", borderRadius: 8, border: "none", background: "#0f172a", color: "#fff", fontSize: 13.5, fontWeight: 600, cursor: saving ? "wait" : "pointer", fontFamily: "inherit", opacity: saving ? 0.7 : 1 }}>
            {saving ? "Reporting…" : "Report Issue"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Issue Detail Modal ──────────────────────────────────────────────────────
function IssueDetailModal({ issue: initialIssue, onClose, onUpdated, existingCategories }) {
  const [issue, setIssue] = useState(initialIssue);
  const [comment, setComment] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [following, setFollowing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showChangeStatus, setShowChangeStatus] = useState(false);
  const [editingWorkaround, setEditingWorkaround] = useState(false);
  const [workaroundText, setWorkaroundText] = useState(issue.workaround || "");
  const [resolutionTime, setResolutionTime] = useState("");
  const [coordinationRating, setCoordinationRating] = useState(0);
  const [satisfactionRating, setSatisfactionRating] = useState(0);
  const [error, setError] = useState("");

  const handleLinkSegment = async seg => {
    try {
      const res = await api.post(`api/platform-issues/issues/${issue.id}/link_segment/`, { segment_id: seg.id, action: "add" });
      const updated = { ...issue, affected_segments: res.data.affected_segments };
      setIssue(updated);
      onUpdated && onUpdated(updated);
    } catch { }
  };

  const handleUnlinkSegment = async segId => {
    try {
      const res = await api.post(`api/platform-issues/issues/${issue.id}/link_segment/`, { segment_id: segId, action: "remove" });
      const updated = { ...issue, affected_segments: res.data.affected_segments };
      setIssue(updated);
      onUpdated && onUpdated(updated);
    } catch { }
  };

  const isClosingStatus = ["RESOLVED", "CLOSED"].includes(newStatus);

  const handleFollow = async () => {
    setFollowing(true);
    try {
      const res = await api.post(`api/platform-issues/issues/${issue.id}/follow/`);
      const updated = { ...issue, is_following: res.data.following, follower_count: res.data.follower_count };
      setIssue(updated);
      onUpdated && onUpdated(updated);
    } catch { } finally { setFollowing(false); }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await api.post(`api/platform-issues/issues/${issue.id}/publish/`);
      const updated = { ...issue, is_published: res.data.is_published };
      setIssue(updated);
      onUpdated && onUpdated(updated);
    } catch { } finally { setPublishing(false); }
  };

  const handleSaveWorkaround = async () => {
    try {
      await api.patch(`api/platform-issues/issues/${issue.id}/`, { workaround: workaroundText });
      const updated = { ...issue, workaround: workaroundText };
      setIssue(updated);
      onUpdated && onUpdated(updated);
      setEditingWorkaround(false);
    } catch { }
  };

  const handleSubmitUpdate = async () => {
    if (!comment.trim()) { setError("Please enter a comment."); return; }
    setSubmitting(true); setError("");
    try {
      const payload = { comment };
      if (showChangeStatus && newStatus) {
        payload.status = newStatus;
        if (isClosingStatus) {
          if (resolutionTime)       payload.resolution_time     = resolutionTime;
          if (coordinationRating)   payload.coordination_rating = coordinationRating;
          if (satisfactionRating)   payload.satisfaction_rating = satisfactionRating;
        }
      }
      const res = await api.post(`api/platform-issues/issues/${issue.id}/add_update/`, payload);
      setIssue(res.data);
      onUpdated && onUpdated(res.data);
      setComment(""); setNewStatus(""); setShowChangeStatus(false);
      setResolutionTime(""); setCoordinationRating(0); setSatisfactionRating(0);
    } catch { setError("Failed to post update."); } finally { setSubmitting(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 720, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.18)", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ padding: "18px 22px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "flex-start", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 7, marginBottom: 8 }}>
              <PlatformChip platform={issue.platform} />
              <TypeChip type={issue.issue_type} />
              {issue.category && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0" }}>
                  <Tag size={9} /> {issue.category}
                </span>
              )}
              <StatusBadge status={issue.status} />
              {issue.is_published && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 3, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600, background: "#f0fdfa", color: "#0d9488", border: "1px solid #99f6e4" }}>
                  <Globe size={9} /> Published
                </span>
              )}
            </div>
            <div style={{ fontSize: 16.5, fontWeight: 700, color: "#0f172a", lineHeight: 1.35 }}>{issue.title}</div>
            <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 4 }}>
              Reported by <span style={{ color: "#475569", fontWeight: 600 }}>{issue.reported_by_name}</span> · {formatTime(issue.created_at)}
              &ensp;·&ensp;<Users size={11} style={{ verticalAlign: "middle" }} /> {issue.follower_count} follower{issue.follower_count !== 1 ? "s" : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            <button onClick={handlePublish} disabled={publishing} title={issue.is_published ? "Unpublish from home page" : "Publish to home page"} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 8, border: `1px solid ${issue.is_published ? "#0d9488" : "#e2e8f0"}`, background: issue.is_published ? "#f0fdfa" : "#fff", color: issue.is_published ? "#0d9488" : "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              {issue.is_published ? <EyeOff size={12} /> : <Globe size={12} />}
              {issue.is_published ? "Unpublish" : "Publish"}
            </button>
            <button onClick={handleFollow} disabled={following} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 8, border: `1px solid ${issue.is_following ? "#6366f1" : "#e2e8f0"}`, background: issue.is_following ? "#eef2ff" : "#fff", color: issue.is_following ? "#6366f1" : "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
              {issue.is_following ? <BellOff size={12} /> : <Bell size={12} />}
              {issue.is_following ? "Unfollow" : "Follow"}
            </button>
            <button onClick={onClose} style={{ border: "1px solid #e2e8f0", background: "#f8fafc", cursor: "pointer", color: "#64748b", padding: "6px", display: "flex", borderRadius: 8 }}><X size={15} /></button>
          </div>
        </div>

        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <div style={{ flex: 1, overflowY: "auto", padding: "18px 22px", display: "flex", flexDirection: "column", gap: 18 }}>
            {/* Description */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Description</div>
              <div style={{ fontSize: 13.5, color: "#334155", lineHeight: 1.6, whiteSpace: "pre-wrap", background: "#f8fafc", borderRadius: 10, padding: "13px 15px", border: "1px solid #e2e8f0" }}>{issue.description}</div>
            </div>

            {/* Workaround */}
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em" }}>Workaround / Partial Fix</div>
                {!editingWorkaround && (
                  <button onClick={() => setEditingWorkaround(true)} style={{ border: "none", background: "transparent", fontSize: 11.5, color: "#6366f1", cursor: "pointer", fontFamily: "inherit", padding: 0 }}>Edit</button>
                )}
              </div>
              {editingWorkaround ? (
                <div>
                  <textarea value={workaroundText} onChange={e => setWorkaroundText(e.target.value)} rows={2} style={{ width: "100%", boxSizing: "border-box", borderRadius: 8, border: "1px solid #e2e8f0", padding: "9px 12px", fontSize: 13, color: "#0f172a", outline: "none", fontFamily: "inherit", resize: "vertical", lineHeight: 1.5, marginBottom: 6 }} />
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={handleSaveWorkaround} style={{ padding: "6px 14px", borderRadius: 7, border: "none", background: "#0f172a", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Save</button>
                    <button onClick={() => { setEditingWorkaround(false); setWorkaroundText(issue.workaround || ""); }} style={{ padding: "6px 14px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 13.5, color: issue.workaround ? "#334155" : "#94a3b8", lineHeight: 1.6, whiteSpace: "pre-wrap", background: "#fffbeb", borderRadius: 10, padding: "13px 15px", border: "1px solid #fef08a", fontStyle: issue.workaround ? "normal" : "italic" }}>
                  {issue.workaround || "No workaround added yet. Click Edit to add one."}
                </div>
              )}
            </div>

            {/* Affected segments */}
            <div style={{ background: "#f8fafc", borderRadius: 10, padding: "13px 15px", border: "1px solid #e2e8f0" }}>
              <SegmentPicker linked={issue.affected_segments || []} onLink={handleLinkSegment} onUnlink={handleUnlinkSegment} />
            </div>

            {/* Resolution note */}
            {issue.resolution_note && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#16a34a", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Resolution</div>
                <div style={{ fontSize: 13.5, color: "#334155", lineHeight: 1.6, whiteSpace: "pre-wrap", background: "#f0fdf4", borderRadius: 10, padding: "13px 15px", border: "1px solid #bbf7d0" }}>{issue.resolution_note}</div>
              </div>
            )}

            {/* Updates timeline */}
            {issue.updates && issue.updates.length > 0 && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12 }}>Updates ({issue.updates.length})</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                  {issue.updates.map((u, i) => (
                    <div key={u.id} style={{ display: "flex", gap: 12 }}>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#475569" }}>
                          {(u.user_name || "?").split(" ").slice(0, 2).map(p => p[0]).join("").toUpperCase()}
                        </div>
                        {i < issue.updates.length - 1 && <div style={{ width: 2, flex: 1, background: "#e2e8f0", margin: "4px 0" }} />}
                      </div>
                      <div style={{ flex: 1, paddingBottom: 14 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 12.5, fontWeight: 700, color: "#0f172a" }}>{u.user_name}</span>
                          {u.status_changed_to && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#f0fdf4", color: "#16a34a", borderRadius: 6, padding: "1px 7px", fontSize: 11, fontWeight: 600 }}>
                              <RefreshCw size={9} /> Status → {getStatus(u.status_changed_to).label}
                            </span>
                          )}
                          <span style={{ fontSize: 11, color: "#94a3b8" }}>{formatTime(u.created_at)}</span>
                        </div>
                        <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.5, whiteSpace: "pre-wrap", background: "#f8fafc", borderRadius: 8, padding: "10px 12px", border: "1px solid #e2e8f0" }}>{u.comment}</div>
                        {/* Resolution feedback */}
                        {(u.resolution_time || u.coordination_rating || u.satisfaction_rating) && (
                          <div style={{ marginTop: 6, background: "#f0fdf4", borderRadius: 8, padding: "10px 12px", border: "1px solid #bbf7d0", display: "flex", flexWrap: "wrap", gap: 16, alignItems: "center" }}>
                            {u.resolution_time && (
                              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12 }}>
                                <Timer size={11} color="#16a34a" />
                                <span style={{ color: "#64748b" }}>Time taken:</span>
                                <span style={{ fontWeight: 700, color: "#16a34a" }}>{u.resolution_time}</span>
                              </div>
                            )}
                            {u.coordination_rating > 0 && (
                              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12 }}>
                                <span style={{ color: "#64748b" }}>Coordination:</span>
                                <span style={{ display: "flex", gap: 2 }}>
                                  {[1,2,3,4,5].map(n => <Star key={n} size={12} fill={u.coordination_rating >= n ? "#f59e0b" : "none"} color={u.coordination_rating >= n ? "#f59e0b" : "#d1d5db"} strokeWidth={1.5} />)}
                                </span>
                              </div>
                            )}
                            {u.satisfaction_rating > 0 && (
                              <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12 }}>
                                <span style={{ color: "#64748b" }}>Satisfaction:</span>
                                <span style={{ display: "flex", gap: 2 }}>
                                  {[1,2,3,4,5].map(n => <Star key={n} size={12} fill={u.satisfaction_rating >= n ? "#f59e0b" : "none"} color={u.satisfaction_rating >= n ? "#f59e0b" : "#d1d5db"} strokeWidth={1.5} />)}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Add update form */}
          <div style={{ padding: "14px 22px", borderTop: "1px solid #e2e8f0", background: "#f8fafc" }}>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Add an update, workaround, or resolution note…"
              rows={2}
              style={{ width: "100%", boxSizing: "border-box", borderRadius: 8, border: "1px solid #e2e8f0", padding: "9px 12px", fontSize: 13, color: "#0f172a", outline: "none", fontFamily: "inherit", background: "#fff", resize: "vertical", lineHeight: 1.5, marginBottom: 10 }}
            />
            {showChangeStatus && (
              <div style={{ marginBottom: 10 }}>
                <select value={newStatus} onChange={e => setNewStatus(e.target.value)} style={{ borderRadius: 8, border: "1px solid #e2e8f0", padding: "7px 10px", fontSize: 13, color: "#0f172a", fontFamily: "inherit", background: "#fff", outline: "none" }}>
                  <option value="">Keep current status</option>
                  {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            )}
            {showChangeStatus && isClosingStatus && (
              <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "14px 16px", marginBottom: 10, display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#16a34a", display: "flex", alignItems: "center", gap: 6 }}>
                  <ThumbsUp size={13} /> Resolution Feedback
                </div>
                {/* Time taken */}
                <div>
                  <div style={{ fontSize: 11.5, fontWeight: 600, color: "#475569", marginBottom: 5, display: "flex", alignItems: "center", gap: 5 }}>
                    <Timer size={12} /> Time taken to resolve
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {RESOLUTION_TIMES.map(t => (
                      <button key={t} type="button" onClick={() => setResolutionTime(t === resolutionTime ? "" : t)}
                        style={{ borderRadius: 100, padding: "4px 12px", fontSize: 12, fontWeight: 500, cursor: "pointer", border: `1px solid ${resolutionTime === t ? "#16a34a" : "#d1fae5"}`, background: resolutionTime === t ? "#16a34a" : "#fff", color: resolutionTime === t ? "#fff" : "#16a34a", fontFamily: "inherit" }}>
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Ratings */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <StarRating value={coordinationRating} onChange={setCoordinationRating} label="Coordination & Support" />
                  <StarRating value={satisfactionRating} onChange={setSatisfactionRating} label="Satisfaction Level" />
                </div>
              </div>
            )}
            {error && <div style={{ fontSize: 12.5, color: "#dc2626", marginBottom: 8 }}>{error}</div>}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <button onClick={() => setShowChangeStatus(v => !v)} style={{ border: "none", background: "transparent", fontSize: 12.5, color: "#64748b", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4, padding: 0 }}>
                <RefreshCw size={12} /> {showChangeStatus ? "Cancel status change" : "Change status"}
              </button>
              <button onClick={handleSubmitUpdate} disabled={submitting} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#0f172a", color: "#fff", fontSize: 13, fontWeight: 600, cursor: submitting ? "wait" : "pointer", fontFamily: "inherit", opacity: submitting ? 0.7 : 1 }}>
                {submitting ? "Posting…" : "Post Update"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Issue Card ──────────────────────────────────────────────────────────────
function IssueCard({ issue, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={() => onClick(issue)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ background: "#fff", borderRadius: 12, border: `1px solid ${hovered ? "#cbd5e1" : "#e2e8f0"}`, padding: "14px 16px", cursor: "pointer", transition: "all 0.15s", transform: hovered ? "translateY(-1px)" : "none", boxShadow: hovered ? "0 4px 16px rgba(0,0,0,0.07)" : "none", position: "relative" }}
    >
      {issue.is_published && (
        <div style={{ position: "absolute", top: 10, right: 10, display: "inline-flex", alignItems: "center", gap: 3, borderRadius: 6, padding: "2px 7px", fontSize: 10, fontWeight: 600, background: "#f0fdfa", color: "#0d9488" }}>
          <Globe size={9} /> Published
        </div>
      )}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 8 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "center" }}>
          <PlatformChip platform={issue.platform} />
          <TypeChip type={issue.issue_type} />
          {issue.category && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 2, borderRadius: 5, padding: "1px 6px", fontSize: 10.5, fontWeight: 600, background: "#f1f5f9", color: "#64748b" }}>
              <Tag size={8} /> {issue.category}
            </span>
          )}
        </div>
        <StatusBadge status={issue.status} />
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a", lineHeight: 1.4, marginBottom: 8, paddingRight: issue.is_published ? 60 : 0 }}>{issue.title}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 11.5, color: "#94a3b8" }}>
        <span>{issue.reported_by_name}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Users size={11} />{issue.follower_count}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 3 }}><MessageSquare size={11} />{issue.update_count}</span>
        <span style={{ marginLeft: "auto" }}>{formatTime(issue.updated_at)}</span>
      </div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────
export default function PlatformIssuesPage() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterPlatform, setFilterPlatform] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [existingCategories, setExistingCategories] = useState([]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get("api/platform-issues/issues/categories/");
      setExistingCategories(res.data || []);
    } catch { }
  }, []);

  const fetchIssues = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterPlatform) params.set("platform", filterPlatform);
      if (filterStatus)   params.set("status", filterStatus);
      if (filterType)     params.set("issue_type", filterType);
      if (filterCategory) params.set("category", filterCategory);
      if (search)         params.set("search", search);
      const res = await api.get(`api/platform-issues/issues/?${params.toString()}`);
      setIssues(res.data);
    } catch { } finally { setLoading(false); }
  }, [filterPlatform, filterStatus, filterType, filterCategory, search]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchIssues(); }, [fetchIssues]);

  const handleIssueUpdated = (updated) => {
    setIssues(prev => prev.map(i => i.id === updated.id ? { ...i, ...updated } : i));
    if (selectedIssue?.id === updated.id) setSelectedIssue(updated);
    fetchCategories();
  };

  const chipStyle = (active, color) => ({
    borderRadius: 100, padding: "5px 13px", fontSize: 12, fontWeight: 500, cursor: "pointer",
    border: `1px solid ${active ? (color || "#0f172a") : "#e2e8f0"}`,
    background: active ? (color ? `${color}18` : "#0f172a") : "#fff",
    color: active ? (color || "#fff") : "#64748b",
    fontFamily: "inherit", lineHeight: "normal", transition: "all 0.13s",
  });

  const openIssues       = issues.filter(i => i.status === "OPEN").length;
  const inProgressIssues = issues.filter(i => i.status === "IN_PROGRESS").length;
  const publishedIssues  = issues.filter(i => i.is_published).length;

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", padding: "28px 32px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 22, gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.3px", marginBottom: 4 }}>Platform Issues</div>
          <div style={{ fontSize: 13, color: "#64748b" }}>Track bugs and challenging logic across XEBO, SurveyToGo, and Forsta</div>
        </div>
        <button onClick={() => setShowCreate(true)} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 18px", borderRadius: 10, border: "none", background: "#0f172a", color: "#fff", fontSize: 13.5, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          <Plus size={15} /> Report Issue
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        {[
          { label: "Open",        count: openIssues,       color: "#ef4444", bg: "#fef2f2" },
          { label: "In Progress", count: inProgressIssues, color: "#f59e0b", bg: "#fffbeb" },
          { label: "Published",   count: publishedIssues,  color: "#0d9488", bg: "#f0fdfa" },
          { label: "Total",       count: issues.length,    color: "#475569", bg: "#f1f5f9" },
        ].map(stat => (
          <div key={stat.label} style={{ background: stat.bg, borderRadius: 10, padding: "9px 16px", display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ fontSize: 19, fontWeight: 700, color: stat.color }}>{stat.count}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: stat.color }}>{stat.label}</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 20, alignItems: "center" }}>
        <div style={{ position: "relative" }}>
          <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search issues…"
            style={{ paddingLeft: 30, paddingRight: 12, paddingTop: 6, paddingBottom: 6, borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, color: "#0f172a", background: "#fff", outline: "none", fontFamily: "inherit", width: 190 }}
          />
        </div>

        <div style={{ width: 1, height: 26, background: "#e2e8f0" }} />

        <button onClick={() => setFilterPlatform("")} style={chipStyle(!filterPlatform)}>All</button>
        {PLATFORMS.map(p => (
          <button key={p.value} onClick={() => setFilterPlatform(p.value === filterPlatform ? "" : p.value)} style={chipStyle(filterPlatform === p.value, p.color)}>{p.label}</button>
        ))}

        <div style={{ width: 1, height: 26, background: "#e2e8f0" }} />

        {STATUSES.map(s => (
          <button key={s.value} onClick={() => setFilterStatus(s.value === filterStatus ? "" : s.value)} style={chipStyle(filterStatus === s.value, s.color)}>{s.label}</button>
        ))}
        {filterStatus && <button onClick={() => setFilterStatus("")} style={chipStyle(false)}>Clear</button>}

        {/* Category filter */}
        {existingCategories.length > 0 && (
          <>
            <div style={{ width: 1, height: 26, background: "#e2e8f0" }} />
            {existingCategories.map(c => (
              <button key={c} onClick={() => setFilterCategory(c === filterCategory ? "" : c)} style={chipStyle(filterCategory === c)}>
                <Tag size={10} style={{ verticalAlign: "middle", marginRight: 3 }} />{c}
              </button>
            ))}
          </>
        )}
      </div>

      {/* Issue list */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8", fontSize: 14 }}>Loading…</div>
      ) : issues.length === 0 ? (
        <div style={{ textAlign: "center", padding: "60px 0", border: "2px dashed #e2e8f0", borderRadius: 16, background: "#fff" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#475569" }}>No issues found</div>
          <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 4 }}>Adjust filters or report a new issue.</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 12 }}>
          {issues.map(issue => (
            <IssueCard key={issue.id} issue={issue} onClick={setSelectedIssue} />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateIssueModal
          onClose={() => setShowCreate(false)}
          onCreated={issue => { setIssues(prev => [issue, ...prev]); fetchCategories(); }}
          existingCategories={existingCategories}
        />
      )}
      {selectedIssue && (
        <IssueDetailModal
          issue={selectedIssue}
          onClose={() => setSelectedIssue(null)}
          onUpdated={handleIssueUpdated}
          existingCategories={existingCategories}
        />
      )}
    </div>
  );
}
